/**
 * 模块: Keyword Market Columns
 * 职责: 关键词表格市场分析列定义（市场类型、主导品牌、广告优先级）
 * 限制: <280行
 */

import React from 'react';
import { Tag, Tooltip } from 'antd';
import type { ColumnType } from 'antd/es/table';
import { metricSorter } from './columnUtils';

/**
 * 市场分析相关列（品牌集中度、市场类型、主导品牌、广告优先级）
 */
export const marketColumns: ColumnType<any>[] = [
  {
    title: <Tooltip title="品牌集中度HHI指数（0-10000），越高越集中">品牌集中度</Tooltip>,
    dataIndex: ['metrics', 'brand_concentration'],
    key: 'brand_concentration',
    align: 'center',
    render: (value: number) => {
      if (value === null || value === undefined) return '-';
      // HHI阈值：<1500竞争性，1500-2500中度集中，>=2500高度集中
      let color = '#52c41a'; // 绿色：竞争性市场
      if (value >= 2500) {
        color = '#ff4d4f'; // 红色：高度集中
      } else if (value >= 1500) {
        color = '#fa8c16'; // 橙色：中度集中
      }
      return (
        <Tooltip title={
          value >= 2500 ? '高度集中市场' : 
          value >= 1500 ? '中度集中市场' : 
          '竞争性市场'
        }>
          <span style={{ color, fontWeight: 'bold' }}>{value.toFixed(0)}</span>
        </Tooltip>
      );
    },
    sorter: (a: any, b: any) => metricSorter(a, b, 'brand_concentration'),
  },
  {
    title: <Tooltip title="市场分类">市场类型</Tooltip>,
    dataIndex: ['metrics', 'market_matrix_type'],
    key: 'market_matrix_type',
    align: 'center',
    render: (value: string) => {
      if (!value) return '-';
      const colorMap: Record<string, string> = {
        '蓝海市场': '#1890ff',
        '蓝海': '#1890ff',
        '红海市场': '#ff4d4f',
        '红海': '#ff4d4f',
        '价格敏感型': '#fa8c16',
        '品牌驱动型': '#722ed1',
      };
      const color = colorMap[value] || 'blue';
      return <Tag color={color} className="market-type-tag">{value}</Tag>;
    },
  },
  {
    title: <Tooltip title="该关键词的主导品牌">主导品牌</Tooltip>,
    dataIndex: ['metrics', 'dominant_brand'],
    key: 'dominant_brand',
    align: 'center',
    ellipsis: true,
  },
  {
    title: <Tooltip title="广告投放优先级(S/A/B/C/D)">投放优先级</Tooltip>,
    dataIndex: ['metrics', 'ad_priority'],
    key: 'ad_priority',
    align: 'center',
    render: (_: any, record: any) => {
      const score = record.metrics?.ad_priority_score;
      const grade = record.metrics?.ad_priority_grade;
      if (score === null || score === undefined) return '-';
      const colorMap: Record<string, string> = {
        S: 'magenta',
        A: 'red',
        B: 'orange',
        C: 'blue',
        D: 'default',
      };
      return (
        <span>
          <Tag color={colorMap[grade] || 'default'}>{grade || '-'}</Tag>
          {score.toFixed(1)}
        </span>
      );
    },
    sorter: (a: any, b: any) => metricSorter(a, b, 'ad_priority_score'),
  },
];

